import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useState } from 'react'
import { X, PanelRightClose, PanelRightOpen } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '@/components/ui/Modal'
import { useComposerStore, type BlockCategory } from '@/stores/composerStore'
import { blockFieldMeta, blockFieldLabel, blockEmoji } from './blockTaxonomy'
import { estimateBlockTokens } from './blockCardStatus'
import { blockAtIdentity } from './composedPromptTargets'
import { useFocusedBlock } from './FocusedBlockContext'
import { CoachRail } from './CoachRail'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'

interface Props {
  personaName: string
  personaId: string
  bundleId: string | null
  hasUnsavedChanges: boolean
  /** SAME save handler as the top bar (index.tsx handleSave): commits basics + commitDraft(). */
  onSave: () => Promise<void>
  /** True while the workshop-level save is in flight (index.tsx `saving`). */
  saving: boolean
  findings: Finding[]
  onJump: (loc: FindingLocation) => void
  avatarUrl?: string | null
}

const SIDE_KEY = 'workshop.focus.sidePanelOpen'

export function BlockFocusEditor({ personaName, personaId, bundleId, hasUnsavedChanges, onSave, saving, findings, onJump, avatarUrl }: Props) {
  const { target, close } = useFocusedBlock()
  const base = useComposerStore((s) => s.draftBundle ?? s.serverBundle)
  const serverBundle = useComposerStore((s) => s.serverBundle)
  const dirtyPaths = useComposerStore((s) => s.dirtyPaths)
  const updateBlock = useComposerStore((s) => s.updateBlock)
  const updateBlockAt = useComposerStore((s) => s.updateBlockAt)
  const revertBlockAt = useComposerStore((s) => s.revertBlockAt)
  const [sideOpen, setSideOpen] = useState(() => localStorage.getItem(SIDE_KEY) !== 'false')
  useEffect(() => { localStorage.setItem(SIDE_KEY, String(sideOpen)) }, [sideOpen])
  const [localSaving, setLocalSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // True once ANY edit was made through THIS modal opening (typing or
  // Insert example). Untouched sessions close without a confirm (spec b).
  const [touchedInSession, setTouchedInSession] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const isOpen = target !== null
  // Reset per-session state on every fresh modal opening (the component
  // stays mounted between openings — it renders null while closed).
  useEffect(() => {
    if (isOpen) {
      setSaveError(null)
      setTouchedInSession(false)
      setConfirmOpen(false)
    }
  }, [isOpen])

  const busy = saving || localSaving
  const handleSave = async (): Promise<boolean> => {
    setLocalSaving(true)
    setSaveError(null)
    try {
      await onSave()
      return true
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed (unknown error)')
      return false
    } finally {
      setLocalSaving(false)
    }
  }

  // Single funnel for every close path (X, Done, ESC, overlay). Only a
  // session that touched the draft AND still has unsaved workshop changes
  // gets the confirm; everything else closes immediately.
  const requestClose = () => {
    if (touchedInSession && hasUnsavedChanges) {
      setConfirmOpen(true)
      return
    }
    close()
  }

  const handleSaveAndClose = async () => {
    const ok = await handleSave()
    setConfirmOpen(false)
    // On failure the editor stays open — the footer Alert shows the error.
    if (ok) close()
  }

  const handleCloseKeepDraft = () => {
    setConfirmOpen(false)
    close()
  }

  if (!target) return null

  const value =
    target.index !== null
      ? base?.blocks[target.index]?.content ?? ''
      : base?.blocks.find((b) => b.category === target.category)?.content ?? ''
  const liveIndex =
    target.index !== null
      ? target.index
      : base?.blocks.findIndex((b) => b.category === target.category) ?? -1
  const baseline = serverBundle
    ? blockAtIdentity(serverBundle.blocks, target.category, target.ordinal)
    : null
  // Reorder-ambiguity guard (same rule as composerStore.revertBlockAt): if
  // the only dirt is a whole-list reorder and this block's category repeats
  // in the live blocks, (category, ordinal) pairs it with its POSITIONAL
  // counterpart — which may be the wrong baseline for a same-category swap.
  // Disable Revert rather than let it silently copy a sibling's content.
  const liveCategory = liveIndex >= 0 ? base?.blocks[liveIndex]?.category : undefined
  const sameCategoryCount = liveCategory
    ? (base?.blocks.filter((b) => b.category === liveCategory).length ?? 0)
    : 0
  const reorderAmbiguous = dirtyPaths.has('block:reorder') && sameCategoryCount > 1
  const canRevert =
    baseline !== null && liveIndex >= 0 && baseline.content !== value && !reorderAmbiguous
  const meta = blockFieldMeta(target.category, personaName)
  const label = blockFieldLabel(target.category, target.ordinal)
  const editorId = 'focus-editor-textarea'

  const onChange = (next: string) => {
    setTouchedInSession(true)
    if (target.index !== null) updateBlockAt(target.index, next)
    else updateBlock(target.category as BlockCategory, next)
  }
  const insertExample = () => {
    const cur = value.trim()
    onChange(cur ? `${cur}\n${meta.example}` : meta.example)
  }

  // Radix dismissal (ESC / outside interaction) is intercepted so it runs
  // through requestClose() like every other close path.
  const handleDismissAttempt = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    requestClose()
  }

  return (
    <>
    <Dialog.Root open onOpenChange={(o) => { if (!o) requestClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay data-testid="focus-editor-overlay" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs data-[state=open]:animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          onEscapeKeyDown={handleDismissAttempt}
          onPointerDownOutside={handleDismissAttempt}
          onInteractOutside={handleDismissAttempt}
          className="fixed left-1/2 top-1/2 z-50 flex h-[86vh] w-[92vw] max-w-6xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-background shadow-2xl"
        >
          {/* header */}
          <div className="flex items-center gap-3 border-b border-border px-5 py-3.5">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
              <span aria-hidden>{blockEmoji(target.category)}</span> {label}
            </span>
            <Dialog.Title className="text-base font-semibold text-foreground">{label}</Dialog.Title>
            <div className="ml-auto flex items-center gap-1">
              <button type="button" onClick={() => setSideOpen((o) => !o)} aria-label={sideOpen ? 'Hide coach & preview' : 'Show coach & preview'} className="rounded-sm p-1.5 text-muted-foreground hover:bg-muted-foreground/10">
                {sideOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </button>
              <button type="button" onClick={requestClose} aria-label="Close editor" className="rounded-sm p-1.5 text-muted-foreground hover:bg-muted-foreground/10">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* body */}
          <div className={`grid min-h-0 flex-1 ${sideOpen ? 'grid-cols-1 lg:grid-cols-[1.7fr_1fr]' : 'grid-cols-1'}`}>
            <div className="flex min-h-0 flex-col overflow-auto p-5">
              <label htmlFor={editorId} className="sr-only">{label}</label>
              <p className="mb-3 text-sm text-muted-foreground">{meta.hint}</p>
              <textarea
                id={editorId}
                aria-label={label}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={meta.example}
                className="min-h-[240px] flex-1 resize-none rounded-lg border border-border bg-background p-4 text-[15px] leading-relaxed text-foreground focus:border-primary focus:outline-hidden focus:ring-2 focus:ring-primary/30"
              />
              {meta.example && (
                <div className="mt-3 rounded-lg border border-dashed border-primary/20 bg-primary/5 p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Example</span>
                    <button type="button" onClick={insertExample} className="text-xs font-semibold text-primary hover:underline">Insert ↩</button>
                  </div>
                  <p className="text-xs italic leading-relaxed text-muted-foreground">{meta.example}</p>
                </div>
              )}
            </div>
            {sideOpen && (
              <div className="hidden min-h-0 flex-col border-l border-border lg:flex">
                <CoachRail personaId={personaId} bundleId={bundleId} hasUnsavedChanges={hasUnsavedChanges} findings={findings} onJump={onJump} avatarUrl={avatarUrl} personaName={personaName} defaultTab="preview" />
              </div>
            )}
          </div>

          {/* footer */}
          <div className="flex flex-col gap-2 border-t border-border bg-primary/5 px-5 py-3">
            {saveError && (
              <Alert variant="destructive" className="text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span>{saveError}</span>
                  <div className="flex shrink-0 gap-1.5">
                    <Button variant="ghost" size="sm" onClick={() => void handleSave()}>
                      Retry
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSaveError(null)}>
                      Dismiss
                    </Button>
                  </div>
                </div>
              </Alert>
            )}
            <div className="flex items-center gap-3">
              {baseline === null ? (
                <span className="text-xs text-muted-foreground">New block — not saved yet</span>
              ) : (
                <button
                  type="button"
                  onClick={() => revertBlockAt(liveIndex)}
                  disabled={!canRevert}
                  className="rounded-lg border border-border px-3 py-2 text-xs font-semibold text-foreground hover:bg-primary/10 disabled:opacity-50"
                >
                  Revert to saved
                </button>
              )}
              <span className="text-xs tabular-nums text-muted-foreground">
                {value.length} characters · ≈ {estimateBlockTokens(value)} tokens
              </span>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={busy || !hasUnsavedChanges}
                className="ml-auto rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={requestClose} className="rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-primary/10">Done</button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>

    {/* Unsaved-changes confirm (spec b) — shared ui/Modal. */}
    <Modal open={confirmOpen} onOpenChange={setConfirmOpen}>
      {/* showClose off: the built-in X (aria-label "Close") would collide with
          the explicit Close action; Keep editing is the cancel affordance. */}
      <ModalContent size="sm" showClose={false}>
        <ModalHeader>
          <ModalTitle>Unsaved changes</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            You have unsaved changes. They&apos;ll stay as a draft on the Workshop page until you save.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
            Keep editing
          </Button>
          <Button variant="ghost" onClick={handleCloseKeepDraft}>
            Close
          </Button>
          <Button variant="default" isLoading={busy} onClick={() => void handleSaveAndClose()}>
            Save &amp; close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  )
}
